/**
 * Shared week-resolution and week-labelling for the MLB matchup surfaces.
 *
 * WHY THIS EXISTS AS A SHARED MODULE
 * MLBBetaMatchups and MLBBetaFantasySchedule have now disagreed about which matchups
 * exist TWICE, and both times the fix landed on only one of them:
 *
 *   2026-08-18  Schedule partitioned on `week > regular_season_weeks` and correctly hid
 *               the phantom post-season rows. Matchups had no such partition and
 *               rendered them as real games.
 *   2026-08-24  Schedule gained a dedicated playoff navigator and showed F6P's week 21.
 *               Matchups accepted only `live` as a default-week signal, so with weeks
 *               1-20 `final` and week 21 `scheduled`, it opened on Week 1 — matchups
 *               from March — and the playoff round was twenty "next" clicks away.
 *
 * Two surfaces deriving the same answer independently is the defect. They import from
 * here now so a third divergence needs someone to deliberately stop using it.
 */

export interface WeekLike {
  week: number;
  status: string;
}

/**
 * Which week should a matchup view open on?
 *
 *   live week  ->  first scheduled  ->  last final  ->  1
 *
 * `live` alone is NOT sufficient, which was the 2026-08-24 bug. There is no live week in
 * any of these states, all of which are normal:
 *   - between rounds (week N finalised, week N+1 not yet flipped to live)
 *   - during the engine's quiet hours (1 AM - 9 AM ET), when nothing gets flipped at all
 *   - after a season completes, permanently
 * Falling through to `1` in those cases sends the user to the season opener.
 */
export function resolveDefaultWeek(matchups: WeekLike[]): number {
  if (!matchups || matchups.length === 0) return 1;

  const live = matchups.find(m => m.status === 'live');
  if (live) return live.week;

  // Earliest scheduled — the next thing that will be played.
  const scheduled = matchups
    .filter(m => m.status === 'scheduled')
    .sort((a, b) => a.week - b.week)[0];
  if (scheduled) return scheduled.week;

  // Nothing upcoming: show the most recent completed week rather than the first.
  // MLB uses 'final', not 'completed' — filtering on the wrong literal here would
  // silently fall through to 1 and look like the bug this function replaces.
  const finals = matchups.filter(m => m.status === 'final');
  if (finals.length > 0) return Math.max(...finals.map(m => m.week));

  return 1;
}

/**
 * Name a playoff round by its distance from the END, so the last round is always the
 * Championship regardless of bracket depth.
 *
 *   3 rounds -> Quarterfinals, Semifinals, Championship
 *   2 rounds -> Semifinals, Championship
 *   1 round  -> Championship
 *
 * Previously a fixed ['Semifinals','Championship','Finals'] indexed by position, which
 * existed three times in MLBBetaFantasySchedule and was only correct for a two-round
 * bracket. F6P has three playoff weeks, so it labelled round one "Semifinals" and put the
 * real title game after something already called "Championship".
 */
export function playoffRoundName(idx: number, totalRounds: number): string {
  const fromEnd = totalRounds - 1 - idx;
  if (fromEnd === 0) return 'Championship';
  if (fromEnd === 1) return 'Semifinals';
  if (fromEnd === 2) return 'Quarterfinals';
  return `Round ${idx + 1}`;
}

/**
 * The playoff weeks present in a set of matchups, ascending.
 */
export function playoffWeeksOf(matchups: WeekLike[], regularSeasonWeeks: number): number[] {
  return [...new Set(
    (matchups || []).filter(m => m.week > regularSeasonWeeks).map(m => m.week),
  )].sort((a, b) => a - b);
}

/**
 * How many rounds a bracket has, derived from the LEAGUE CONFIG — never from how many
 * playoff weeks currently have rows.
 *
 * This was the 2026-08-24 bug on F6P's Playoffs tab. Round count was taken from the rounds
 * that existed, and only week 21 existed, so it computed totalRounds = 1 and named round
 * one "Championship" (index 0 IS the last round when there is only one). Worse, the labels
 * would have shifted every week as new rows appeared: 1 round -> "Championship",
 * 2 rounds -> "Semifinals, Championship", so week 21 would have been renamed twice.
 *
 * The bracket size is a property of the league, known from the moment it locks:
 *   6 teams -> bracket of 8 -> 3 rounds
 *   4 teams -> bracket of 4 -> 2 rounds
 *   8 teams -> bracket of 8 -> 3 rounds
 */
export function totalPlayoffRounds(playoffTeams: number): number {
  const n = Math.max(playoffTeams || 0, 2);
  return Math.max(1, Math.ceil(Math.log2(n)));
}

/**
 * Which round a playoff week is, counting from the first playoff week.
 *
 * Derived from the week NUMBER, not from a position in a list of existing weeks. Using
 * indexOf against the rows that happen to exist means a missing or not-yet-created week
 * silently shifts every later label — the same class of mistake as deriving the round
 * count from existing rows.
 */
export function playoffRoundIndex(week: number, regularSeasonWeeks: number): number {
  return week - regularSeasonWeeks - 1;
}

/**
 * How a week should be titled in a navigator: "Week 12" during the regular season, or the
 * round name once past it — so every surface names the same week the same way.
 *
 * Takes playoffTeams (league config) rather than the matchup list, so the answer does not
 * change as rows are created.
 */
export function weekLabel(
  week: number,
  regularSeasonWeeks: number,
  playoffTeams: number,
): string {
  if (!regularSeasonWeeks || week <= regularSeasonWeeks) return `Week ${week}`;

  const idx = playoffRoundIndex(week, regularSeasonWeeks);
  const total = totalPlayoffRounds(playoffTeams);
  if (idx < 0 || idx >= total) return `Week ${week}`;

  return playoffRoundName(idx, total);
}
